/**
 * Patient messaging — templates, variable substitution and SMS segment arithmetic.
 *
 * BOTH CHANNELS SEND, and they no longer send through the same place. SMS goes to
 * parsina.sms.microservice (Kavenegar); WhatsApp goes to MeshHub's shared gateway.
 * From here that split is invisible on purpose — both are `sendPatientMessage` /
 * `startPatientBroadcast` on the clinic BFF (see `hooks/usePatientMessaging.js`), and
 * the compose surface picks a channel, not a transport. BALE is a third channel on the SMS
 * path: the same Kavenegar account and the same service, with Parsina's Bale bot as the
 * sender instead of an SMS line.
 *
 * The «به‌زودی» that used to sit on SMS is GONE, because the provider landed. It was
 * never a hidden channel — PATIENTS_PAGE_REDESIGN.md §8.2 (D5) argued that a declared
 * but unselectable channel keeps the roadmap legible where an absent one does not —
 * and the promise made there was that it would become selectable behind the same
 * GraphQL shape, with nothing in the compose surface changing. That is what happened.
 *
 * WHAT THIS MODULE DOES *NOT* DECIDE IS THE PRICE. The پارسه cost of an SMS comes from
 * the service that will make the debit (`smsQuote` → `useSmsQuote`), because a figure
 * printed here that disagrees with the ledger is worse than no figure at all. The
 * SEGMENT count below stays local: it is what the operator edits against, keystroke by
 * keystroke, and `internal/sms/segments.go` in the service is its deliberate mirror —
 * change one and change the other, or the two stop agreeing about what a message costs.
 *
 * Audience arithmetic — who can actually be reached, and who is excluded and why —
 * lives in `screens/Patients/patientsView.js`, because reachability is a property of
 * the patient's clinic relationship rather than of the message. This module owns the
 * MESSAGE: its templates, its variables and what it will cost to send.
 *
 * Two numbers decide whether the desk trusts this feature, and both are computed here
 * rather than in the view:
 *
 *  1. **Segments, not characters.** An Iranian gateway bills per SMS *segment*. Persian
 *     text is UCS-2, where a segment is 70 characters — 67 once the message spills into
 *     a second one, because concatenation spends 3 characters per part on the UDH that
 *     reassembles them. A 71-character Persian message is two segments, not one, and a
 *     counter that shows only «۷۱ کاراکتر» understates the bill by 100%.
 *
 *  2. **Segments AFTER substitution.** `{نام بیمار}` is 11 characters in the box and
 *     «محمدرضا حسینی‌نژاد» is 18 on the wire. Estimating from the template is what makes
 *     a broadcast cost more than the number the operator approved, so the estimate here
 *     resolves the template per recipient and adds up the real segment counts.
 */

import { phone as phoneLib } from '@parsina/framework';

// ── SMS encoding ─────────────────────────────────────────────────────────────────

// GSM 03.38 basic set — one septet each. Anything outside it (any Persian letter, and
// the Persian digits ۰-۹) forces the whole message to UCS-2, which is why a single
// stray Persian character in an otherwise-Latin message more than halves its capacity.
const GSM7 = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡'
  + 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);

// Escaped in GSM-7: two septets each.
const GSM7_EXT = new Set('^{}\\[~]|€');

export const SMS_CAPACITY = {
  gsm7: { single: 160, concatenated: 153 },
  ucs2: { single: 70,  concatenated: 67 },
};

/**
 * How the gateway will bill one message body.
 *
 * `units` is the billed length in the message's own alphabet (septets for GSM-7,
 * UTF-16 code units for UCS-2) — deliberately not `String.length`, which counts an
 * escaped `{` once and a non-BMP emoji twice.
 */
export function smsSegments(text) {
  const s = String(text ?? '');
  if (!s) return { encoding: 'gsm7', chars: 0, units: 0, segments: 0, capacity: SMS_CAPACITY.gsm7.single };

  let septets = 0;
  let gsm = true;
  for (const ch of s) {
    if (GSM7.has(ch)) septets += 1;
    else if (GSM7_EXT.has(ch)) septets += 2;
    else { gsm = false; break; }
  }

  const cap = gsm ? SMS_CAPACITY.gsm7 : SMS_CAPACITY.ucs2;
  // UCS-2 bills UTF-16 code units, so a surrogate pair (emoji) legitimately costs two.
  const units = gsm ? septets : s.length;
  const segments = units <= cap.single ? 1 : Math.ceil(units / cap.concatenated);

  return {
    encoding: gsm ? 'gsm7' : 'ucs2',
    chars: [...s].length,
    units,
    segments,
    capacity: segments > 1 ? cap.concatenated : cap.single,
  };
}

// ── Variables ────────────────────────────────────────────────────────────────────

/**
 * The variables every message can use, and the three that need an appointment.
 *
 * A variable that silently empties is worse than an absent one. `{تاریخ نوبت}` and its
 * siblings substitute to nothing for a patient with no upcoming نوبت, so they are
 * **conditional**: offered only when compose was opened with an appointment context —
 * from a Reception or Calendar row, or for recipients who each have a `nextAppointment`
 * — and the recipients who have none are excluded with a stated reason rather than sent
 * a sentence with a hole in it (§8.3, decision D9). `varsForContext` is the gate; the
 * view never reads `MESSAGE_VARS` directly.
 *
 * The tokens below are exempt from the string audit. They are substitution markers
 * matched literally against the message the operator typed, not panel chrome, and they
 * must stay byte-stable: translate the patient-name token and every message already
 * composed silently stops substituting. The labels beside them ARE chrome, and are keys.
 *
 * The exemption marker has to be its own bracket-free line — the audit exempts the
 * marker plus the following balanced block, so a brace or parenthesis anywhere in the
 * comment closes that block early and re-flags the very lines it was meant to cover.
 */
// i18n-exempt: literal substitution markers, byte-stable by design — see above.
export const MESSAGE_VARS = [
  { token: '{نام بیمار}',    labelKey: 'patients.messaging.vars.patientName', resolve: (ctx) => ctx.patient?.name || ctx.fallbackName || '' },
  { token: '{شماره پرونده}', labelKey: 'patients.messaging.vars.fileNumber',  resolve: (ctx) => ctx.patient?.fileNumber || '' },
  { token: '{نام کلینیک}',   labelKey: 'patients.messaging.vars.clinicName',  resolve: (ctx) => ctx.clinicName || '' },
];

// i18n-exempt: literal substitution markers, byte-stable by design — see above.
export const APPOINTMENT_VARS = [
  { token: '{تاریخ نوبت}', labelKey: 'patients.compose.vars.appointmentDate', resolve: (ctx) => ctx.appointmentDate || '' },
  { token: '{ساعت نوبت}',  labelKey: 'patients.compose.vars.appointmentTime', resolve: (ctx) => ctx.appointmentTime || '' },
  { token: '{نام پزشک}',   labelKey: 'patients.compose.vars.doctorName',      resolve: (ctx) => ctx.doctorName || '' },
];

export const ALL_VARS = [...MESSAGE_VARS, ...APPOINTMENT_VARS];

/** Which variables the operator may insert right now. */
export const varsForContext = ({ hasAppointmentContext = false } = {}) =>
  (hasAppointmentContext ? ALL_VARS : MESSAGE_VARS);

/** True when a body needs an appointment to render fully. */
export const usesAppointmentVars = (body) => {
  const s = String(body ?? '');
  return APPOINTMENT_VARS.some((v) => s.includes(v.token));
};

/**
 * Substitutes every known token. Unknown `{...}` text is left alone — it is the
 * operator's. A patient with no name resolves to a polite form of address rather than
 * to an empty string, so «{نام بیمار} عزیز» never sends as « عزیز» (§15.14).
 */
export function renderMessage(body, ctx = {}) {
  return ALL_VARS.reduce(
    (out, v) => out.split(v.token).join(v.resolve(ctx)),
    String(body ?? ''),
  );
}

/** Which variables a body actually uses — drives the "will be replaced" hint. */
export function usedVars(body) {
  const s = String(body ?? '');
  return ALL_VARS.filter((v) => s.includes(v.token));
}

// ── Channels ─────────────────────────────────────────────────────────────────────

/**
 * `order` is the order the cards render in, and it is not cosmetic: the channel that
 * can actually send is FIRST and is the default. The previous screen opened on SMS,
 * so the operator's first «ارسال» always failed (finding F9).
 *
 * `provider` is the one thing that decides whether a channel is selectable. A channel
 * without one still renders — as «به‌زودی», with `soonHintKey` saying what it waits
 * on — because a declared channel keeps the roadmap legible where an absent one does
 * not (§8.2, D5). That was SMS until Kavenegar landed.
 */
export const CHANNELS = {
  whatsapp: {
    key: 'whatsapp',
    order: 0,
    labelKey: 'patients.messaging.channels.whatsapp.label',
    hintKey: 'patients.messaging.channels.whatsapp.hint',
    segmented: false,
    /** Whether a provider exists at all; the LINK state is asked of the session. */
    provider: true,
    /**
     * Billed per MESSAGE (0.1 پارسه by default) from the SENDER's own wallet, by the
     * clinic BFF — reserved before the send, charged on accept. Quoted by
     * `whatsappQuote` from the recipient count alone: no segments, no tariff.
     */
    billed: true,
  },
  sms: {
    key: 'sms',
    order: 1,
    labelKey: 'patients.messaging.channels.sms.label',
    hintKey: 'patients.messaging.channels.sms.hint',
    /** Billed per segment; the counter shows segments and the estimate shows a total. */
    segmented: true,
    provider: true,
    /** Priced by the SMS service, which the compose surface asks for a quote. */
    billed: true,
    soonHintKey: 'patients.compose.channel.smsSoonHint',
  },
  /**
   * Parsina's Bale bot, sent through the same Kavenegar account and the same SMS
   * service as SMS — the sender is the only difference on the wire. Billed per MESSAGE
   * and quoted by that service like SMS; not segmented, because Bale delivers a long
   * body whole. There is no fallback to SMS: a patient without Bale is a failed,
   * unbilled row the desk can resend by SMS.
   */
  bale: {
    key: 'bale',
    order: 2,
    labelKey: 'patients.messaging.channels.bale.label',
    hintKey: 'patients.messaging.channels.bale.hint',
    segmented: false,
    provider: true,
    billed: true,
  },
};

/** The channels in render order — available first (§8.2). */
export const CHANNEL_ORDER = Object.values(CHANNELS)
  .sort((a, b) => a.order - b.order)
  .map((c) => c.key);

// SMS and Bale leave through Kavenegar, so they need the country's SMS provider too.
const SMS_SERVICE_CHANNELS = new Set(['sms', 'bale']);

/**
 * Whether the clinic's COUNTRY offers a channel (PM-0218). The clinic BFF refuses one
 * it does not — NOT_AVAILABLE_IN_COUNTRY — so the panel does not offer it either: an
 * Iraqi clinic has no Bale. `capabilities` is `myClinic.capabilities`
 * (hooks/useClinicCapabilities.js); while it is unknown (null) every channel is
 * offered, as before. A channel is offered when the country lists it in
 * `messagingChannels`, and SMS and Bale only with an `smsProvider` as well.
 *
 * This only HIDES. Whether a channel can send right now — WhatsApp linked, the
 * sender's پارسه — is still each surface's own question.
 */
export function channelOffered(capabilities, key) {
  if (!capabilities) return true;
  const listed = (capabilities.messagingChannels || []).includes(key);
  return SMS_SERVICE_CHANNELS.has(key) ? listed && Boolean(capabilities.smsProvider) : listed;
}

/** CHANNEL_ORDER, narrowed to the channels the clinic's country offers. */
export const offeredChannelOrder = (capabilities) =>
  CHANNEL_ORDER.filter((key) => channelOffered(capabilities, key));

// ── Recipients ───────────────────────────────────────────────────────────────────

/**
 * The patients table pads a missing phone to «—» for display. A recipient list must not
 * inherit that padding: «—» is not a number, and a row carrying it can receive nothing.
 */
export function recipientPhone(patient) {
  const raw = String(patient?.phone ?? '').trim();
  return !raw || raw === '—' ? '' : raw;
}

export const isReachable = (patient) => recipientPhone(patient) !== '';

/**
 * An Iranian mobile number as WhatsApp wants it: `989121234567`, no plus.
 *
 * WhatsApp addresses accounts in E.164, and every part of this product invites the
 * other format — the placeholder on the pairing form reads «۰۹۱۲۱۲۳۴۵۶۷», the patients
 * table stores whatever reception typed, and an Iranian desk writes `0912…` on paper.
 * Handing that to the gateway verbatim asks WhatsApp about a number that does not
 * exist, which fails in the least helpful way available: silently, on someone else's
 * socket.
 *
 * THE RULE IS THE KIT'S. `phone.normalizePhone` is the platform's canonicaliser — the
 * same one `patients.phone` is written with, and byte-identical to the
 * `normalize_phone` the services use — and it REFUSES a string it does not understand
 * where the hand-written rule that used to stand here STRIPPED the junk out of one.
 * That difference is not cosmetic, because this value is a RECIPIENT: `'09123456789abc'`
 * was silently trimmed into a real mobile and handed to the gateway, and two patients
 * whose numbers differed only by junk collapsed onto a single number. Refusing is what
 * the BFF's own audience builder (`src/lib/patientAudience.js`) assumes of it.
 *
 * Iran only, deliberately — the kit would accept a foreign E.164 and this caller has no
 * use for one. `allowLandline` is NOT passed and must never be: Iran's mobile pattern is
 * exactly what keeps a landline, which cannot hold a WhatsApp account, out of here.
 *
 * Returns '' for anything that is not a plausible Iranian mobile, so the caller can say
 * so before opening a stream rather than waiting three minutes for a code that is never
 * coming.
 */
export function toE164Iran(raw) {
  const e164 = phoneLib.normalizePhone(raw);
  return e164.startsWith('+98') ? e164.slice(1) : '';
}

/** Above this many recipients the send asks for an explicit tick first. */
export const BULK_CONFIRM_THRESHOLD = 25;

// ── Cost estimate ────────────────────────────────────────────────────────────────

/**
 * Segments for a body across a known set of recipients — each one resolved with its own
 * name and file number, because that is what the gateway will actually bill.
 *
 * `unknownCount` covers the «همه بیماران» case: the screen holds one page of a registry
 * that may run to thousands, so the rest are priced at the per-recipient worst case seen
 * among the loaded ones and the result is flagged `approximate`.
 */
export function estimateSegments(body, recipients = [], { clinicName = '', unknownCount = 0 } = {}) {
  const perRecipient = recipients.map((p) => ({
    id: p.id,
    segments: smsSegments(renderMessage(body, { patient: p, clinicName })).segments,
  }));

  const known = perRecipient.reduce((sum, r) => sum + r.segments, 0);
  const worst = perRecipient.reduce((max, r) => Math.max(max, r.segments), 0)
    // With nothing loaded to measure, fall back to the raw template.
    || smsSegments(renderMessage(body, { clinicName })).segments;

  return {
    perRecipient,
    totalSegments: known + worst * unknownCount,
    maxSegments: worst,
    approximate: unknownCount > 0,
  };
}

export default {
  smsSegments, renderMessage, usedVars, estimateSegments,
  recipientPhone, isReachable, toE164Iran,
  varsForContext, usesAppointmentVars,
  MESSAGE_VARS, APPOINTMENT_VARS, ALL_VARS,
  CHANNELS, CHANNEL_ORDER, SMS_CAPACITY, BULK_CONFIRM_THRESHOLD,
  channelOffered, offeredChannelOrder,
};
